import { forwardRef } from 'preact/compat'
import { BRAND_CLASSES } from '@/constants/theme'
import { getLabelClasses, type LabelSize } from './formLabelUtils'

export interface NumberInputProps {
    /** Field label */
    label?: string
    /** Whether field is required */
    required?: boolean
    /** Field name for form registration */
    name: string
    /** Error message to display */
    error?: string
    /** Helper text to display below input */
    helperText?: string
    /** Custom class name for container */
    containerClassName?: string
    /** Custom class name for input */
    className?: string
    /** Label size variant */
    labelSize?: LabelSize
    /** Minimum value */
    min?: number | string
    /** Maximum value */
    max?: number | string
    /** Step increment */
    step?: number | string
    /** Placeholder text */
    placeholder?: string
    /** onChange handler */
    onChange?: (e: Event) => void
    /** onBlur handler */
    onBlur?: (e: Event) => void
    /** Additional props passed to input */
    [key: string]: unknown
}

/**
 * Common NumberInput component for numeric input fields
 * Integrates with React Hook Form via forwardRef
 * 
 * @example
 * ```tsx
 * <NumberInput
 *   label="Lead Goal"
 *   min={0}
 *   step={1}
 *   error={errors.leadGoal?.message}
 *   helperText="Optional numeric goal"
 *   {...register('leadGoal', {
 *     validate: (v) => {
 *       if (!v) return true
 *       const n = Number(v)
 *       return !isNaN(n) && n >= 0 || 'Must be >= 0'
 *     }
 *   })}
 * />
 * ```
 */
export const NumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
    ({ label, required, error, helperText, containerClassName, className, labelSize, ...props }, ref) => {
        const inputClasses = `
            w-full px-3 py-2 border rounded-lg
            ${BRAND_CLASSES.INPUT.NORMAL}
            ${BRAND_CLASSES.INPUT.PLACEHOLDER}
            focus:outline-none focus:ring-2 focus:border-transparent
            ${error 
                ? BRAND_CLASSES.INPUT.ERROR 
                : BRAND_CLASSES.FOCUS.PRIMARY
            }
            ${className || ''}
        `.trim().replace(/\s+/g, ' ')

        return (
            <div className={containerClassName}>
                {label && (
                    <label className={getLabelClasses(labelSize)}>
                        {label}
                        {required && <span className={BRAND_CLASSES.INPUT.REQUIRED}> *</span>}
                    </label>
                )}
                <input
                    ref={ref}
                    type="number"
                    inputMode="numeric"
                    {...props}
                    className={inputClasses}
                />
                {error && (
                    <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.ERROR_TEXT}`}>
                        {error}
                    </p>
                )}
                {!error && helperText && (
                    <p className={`mt-1 text-sm ${BRAND_CLASSES.INPUT.HELPER}`}>
                        {helperText}
                    </p>
                )}
            </div>
        )
    }
)

NumberInput.displayName = 'NumberInput'
